import { AfterViewInit, Component, OnInit, ViewChild, computed, inject } from '@angular/core';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { Observable, filter, map, startWith } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntil } from 'rxjs/operators';

import { IframeService, StorageKey, StorageService } from '@lib/core';
import { ScrollService, SubscriptionHelper } from '@lib/util';
import { BasketService, BasketStore } from '@lib/shared/basket-service';
import { SuppliersBasketStore } from '@lib/shared/basket-service/suppliers-basket/data-access';
import { EnvService } from '@lib/env';
import { MicrosoftLoginAppUrlService, OAuthSessionManager } from '@lib/auth';
import {
    Basket,
    BasketItemRequest,
    SupplierBasket,
} from '@lib/basket/suppliers-basket/basket-supplier/basket-supplier-item/ui';

@Component({
    selector: 'eproc-root',
    templateUrl: './app.component.html',
    //TODO CHECK IF WE NEED SuppliersBasketStore HERE
    providers: [BasketStore, SuppliersBasketStore],
    standalone: false,
})
export class AppComponent extends SubscriptionHelper implements AfterViewInit, OnInit {
    private readonly nativeSession = inject(OAuthSessionManager);
    private readonly router = inject(Router);
    private readonly currentUrl = toSignal(this.router.events.pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map((event) => event.urlAfterRedirects), startWith(this.router.url),
    ));
    public readonly hideProtectedContent = computed(() => this.nativeSession.nativeBlocked()
        && !this.currentUrl()?.split('?')[0].endsWith('/client/login'));
    private readonly basketService = inject(BasketService);
    private readonly scrollService = inject(ScrollService);
    private readonly basketStore = inject(BasketStore);
    private readonly storage = inject(StorageService);
    private readonly env = inject(EnvService);
    private readonly microsoftLoginAppUrlService = inject(MicrosoftLoginAppUrlService);

    @ViewChild('basketNav') public basketNav: MatSidenav;
    @ViewChild('content') public content: MatSidenavContent;

    supplierBaskets$!: Observable<SupplierBasket[]>;
    basket$!: Observable<Basket>;

    public loading$: Observable<boolean>;

    ngOnInit() {
        void this.microsoftLoginAppUrlService.init();
        this.listenToScroll();
        this.streamSupplierBaskets();
        this.streamBaskets();
        this.streamDebug();
    }

    closeBasketNav(): void {
        this.basketService.closeBasketSideNav();
    }

    ngAfterViewInit(): void {
        if (!IframeService.inIframe()) {
            this.initBasket();
        }
    }

    streamSupplierBaskets(): void {
        this.supplierBaskets$ = this.basketStore.supplierBaskets;
    }

    streamBaskets(): void {
        this.basket$ = this.basketStore.basket;
    }

    deleteItem(item: BasketItemRequest) {
        this.basketStore.deleteBasketItem$(item);
    }

    private initBasket(): void {
        this.basketService.basketSidenavObserver$.pipe(takeUntil(this.onDestroy$)).subscribe((isOpen) => {
            if (this.basketNav) {
                isOpen ? this.basketNav.open() : this.basketNav.close();
            }
        });
    }

    private listenToScroll(): void {
        this.scrollService.scrollToY$.subscribe((yCoordinate: number) => {
            const contentRect = this.content.getElementRef().nativeElement.getBoundingClientRect().top;
            const elementPosition = yCoordinate - contentRect;

            this.content.getElementRef().nativeElement.scroll({
                top: elementPosition + this.content.getElementRef().nativeElement.scrollTop,
                behavior: 'smooth',
            });
        });
    }

    private streamDebug() {
        this.storage.getItem$<string>(StorageKey.EPROC_DEBUG).subscribe((value) => {
            this.env.isDebug = value === 'true';
        });
    }
}
